import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Security } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import axios from "axios";

import { UserContextWrapper } from "shared/context/UserContext";
import { ConstantsContextWrapper } from "shared/context/ConstantsContext";
import SiteDownErrorBoundary from "shared/components/errors/SiteDownErrorBoundary";
import SharedSiteDown from "shared/components/errors/SharedSiteDown";
import useApiHealthCheck from "shared/hooks/useApiHealthCheck";
import RMERouter from "./components/router/RMERouter";
import {
  buildOktaAuthConfig,
  registerOktaAuthInterceptor,
} from "shared/helpers/OktaAuthHelpers";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

let appInsights = null;

export default function App() {
  const { apiIsDown, isChecking, handleRetry } = useApiHealthCheck();
  const navigate = useNavigate();
  const oktaAuth = useMemo(() => {
    return new OktaAuth(
      buildOktaAuthConfig(
        process.env.REACT_APP_OKTADomain,
        process.env.REACT_APP_OKTAClientID
      )
    );
  }, []);
  const restoreOriginalUri = useCallback(
    async (_oktaAuth, originalUri) => {
      navigate(toRelativeUrl(originalUri, window.location.origin), {
        replace: true,
      });
    },
    [navigate]
  );
  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 3, // 3 minutes - if the data is older than this, it will be refetched in the background
          cacheTime: 1000 * 60 * 5, // 5 minutes - making this longer that staletime so if data older than staletime it can refetch silenty in the background, while still using the cache
          retry: (failureCount, error) => {
            //console.log("error", error);

            // Don't retry 4xx client errors (bad request, unauthorized, not found, etc.)
            if (
              error &&
              error.response &&
              error.response.status >= 400 &&
              error.response.status < 500
            ) {
              return false;
            }

            // Don't retry timeout errors
            if (
              error &&
              (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT")
            ) {
              return false;
            }

            //by default, do not retry on timeout errors as this would cause the UI to hang for long time periods
            if (error && error.code === 408) {
              return false; // Do not retry on timeout errors
            }

            // Retry up to 2 times for other errors
            return failureCount < 1;
          },
        },
      },
    });
  }, []);

  useEffect(() => {
    return registerOktaAuthInterceptor(axios, oktaAuth);
  }, [oktaAuth]);

  useEffect(() => {
    // App booted successfully — allow future stale-asset reloads (MPI-737).
    sessionStorage.removeItem("rmeAssetReloaded");
  }, []);

  // Show loading state while checking API
  if (isChecking) {
    return (
      <SiteDownErrorBoundary>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Checking service availability...</span>
          </div>
        </div>
      </SiteDownErrorBoundary>
    );
  }

  // Show SiteDown component if API is down
  if (apiIsDown) {
    return (
      <SiteDownErrorBoundary>
        <SharedSiteDown onRetry={handleRetry} />
      </SiteDownErrorBoundary>
    );
  }

  return (
    <SiteDownErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <UserContextWrapper>
          <ConstantsContextWrapper>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              <RMERouter appInsights={appInsights} />
            </Security>
          </ConstantsContextWrapper>
        </UserContextWrapper>
      </QueryClientProvider>
    </SiteDownErrorBoundary>
  );
}
